import { useRef, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { ApiError } from "@/lib/api/errors";
import { downloadFile, uploadFile } from "@/lib/api/files";
import { request } from "@/lib/api/client";
import type { ServiceName } from "@/lib/api/services";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime } from "@/lib/format";
import { SessionImport } from "./SessionImport";

/**
 * Ommaviy import (MIG-01): avval tovarlar, keyin kataklar reyestri,
 * so'ng boshlang'ich qoldiq — keyingisi oldingisiga tayanadi.
 *
 * Fayl (XLSX/CSV) serverda o'qiladi. Har doim AVVAL tekshiruv
 * (`commit=false`): bitta xato qator ham butun paketni rad etadi
 * (IMP-001), xatolar qator, ustun va sabab bilan ko'rsatiladi. Faqat
 * tekshiruvdan o'tgan fayl yoziladi — va yozish alohida tasdiq bilan.
 */

type Kind = "sku" | "locations" | "opening" | "session";

interface ImportError {
  row_number: number;
  sheet?: string;
  field?: string;
  value?: string;
  error_code: string;
  message: string;
}

interface ControlCheck {
  name: string;
  expected: number;
  actual: number;
  passed: boolean;
  blocking: boolean;
  rule?: string;
}

interface Batch {
  id: string;
  status: string;
  source_file?: string;
  total_rows: number;
  valid_rows?: number;
  rejected_rows: number;
  committed_rows?: number;
  lp_count?: number;
  movement_count?: number;
  replayed?: boolean;
  control_totals?: { passed: boolean; checks: ControlCheck[] };
  created_at?: string;
}

interface KindSpec {
  label: string;
  step: number;
  service: ServiceName;
  base: string;
  permission: string;
  columns: string;
  /** Rad etilgan paket xatolari XLSX da (bo'lsa). */
  errorReport?: (id: string) => string;
  /** Namuna fayl manzili — importdan OLDIN yuklab olinadi. */
  template?: string;
  hint: string;
}

const KINDS: Record<Kind, KindSpec> = {
  sku: {
    label: "Tovarlar",
    step: 1,
    service: "masterData",
    base: "/api/v1/master-data/imports",
    permission: PERM.masterDataImport,
    columns: "artikul*, nomi*, birlik*, ruscha nom, guruh, ABC, XYZ, uzunlik/eni/balandlik sm, vazn kg, ИКПУ, ТН ВЭД, shtrix-kodlar",
    hint: "Dublikat artikul va shtrix-kod, EAN-13 nazorat raqami, VGH formati tekshiriladi.",
  },
  locations: {
    label: "Kataklar reyestri",
    step: 2,
    service: "topology",
    base: "/api/v1/locations/imports",
    permission: PERM.topologyImport,
    columns: "kod*, zona*, vazifa* (Otbor/Xraneniya), korpus, eski kod, ABC zona, katak turi, o'lchamlar, sig'im, ketma-ketlik, koordinatalar",
    errorReport: (id) => `/api/v1/locations/imports/${id}/duplicates.xlsx`,
    // Namuna fayl: ustun nomlari importchi taniydiganidek. Bo'sh
    // varaqdan boshlagan mutaxassis nomlarni taxmin qilib,
    // «ustun topilmadi» xatosiga urilardi.
    template: "/api/v1/locations/imports/template.xlsx",
    hint: "Kod formati, korpus va vazifa mosligi, dublikatlar va nazorat yig'indilari (A 13 642 / B 523 / C 228, jami 14 393).",
  },
  opening: {
    label: "Boshlang'ich qoldiq",
    step: 3,
    service: "inventory",
    base: "/api/v1/opening-balances/imports",
    permission: PERM.inventoryAdjust,
    columns: "1C ID, artikul yoki shtrix-kod, birlik, miqdor*, katak*, LP, holat, partiya",
    errorReport: (id) => `/api/v1/opening-balances/imports/${id}/errors.xlsx`,
    hint: "Noma'lum tovar/katak, qadoqqa karrali bo'lmagan miqdor, band LP, OX da ikkinchi tovar (ZON-02). Bir xil fayl qayta yozilmaydi.",
  },
  // Excel orqali prixod / vozvrat (2026-11-01) — o'z oqimi (SessionImport).
  session: {
    label: "Prixod / vozvrat",
    step: 4,
    service: "inbound",
    base: "/api/v1/receiving-sessions/imports",
    permission: PERM.inboundApprove,
    columns: "Pallet*, Katak*, Shtrixkod yoki Artikul*, Miqdor*, Brak miqdori, Brak sababi, Izoh",
    hint: "TSD dagi sessiya bilan bir xil qoidalar.",
  },
};

interface Outcome {
  batch?: Batch;
  rejected?: { message: string; batchId?: string; failedTotals?: string[] };
  errors?: ImportError[];
  moreErrors?: boolean;
  committed?: boolean;
}

export function Imports() {
  const session = useSession();
  // Prixod/vozvrat importi — ombor rahbari yoki dispetcher.
  const canKind = (key: Kind) =>
    key === "session"
      ? session.can(PERM.inboundApprove) || session.can(PERM.inboundArrive)
      : session.can(KINDS[key].permission);
  const allowed = (Object.keys(KINDS) as Kind[]).filter(canKind);
  const [kind, setKind] = useState<Kind>(allowed[0] ?? "sku");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [confirming, setConfirming] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const spec = KINDS[kind];

  const reset = () => {
    setOutcome(null);
    setFailure(null);
  };

  const run = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    reset();
    try {
      const batch = await uploadFile<Batch>(spec.service, `${spec.base}/file`, file, { commit: String(commit) });
      setOutcome({ batch, committed: commit });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 422 && cause.body.details?.batch_id) {
        const batchId = String(cause.body.details.batch_id);
        const [errors, batch] = await Promise.all([
          request<{ items: ImportError[]; has_more?: boolean }>(spec.service, `${spec.base}/${batchId}/errors`, {
            query: { limit: 200 },
          }).catch(() => ({ items: [] as ImportError[], has_more: false })),
          request<Batch>(spec.service, `${spec.base}/${batchId}`).catch(() => undefined),
        ]);
        setOutcome({
          batch,
          rejected: {
            message: cause.body.message,
            batchId,
            failedTotals: cause.body.details.failed_control_totals as string[] | undefined,
          },
          errors: errors.items,
          moreErrors: errors.has_more,
        });
      } else {
        setFailure(cause instanceof Error ? cause.message : String(cause));
      }
    } finally {
      setBusy(false);
    }
  };

  const errorColumns: Array<Column<ImportError>> = [
    {
      key: "row",
      header: "Qator",
      align: "right",
      render: (row) => (row.row_number === 0 ? "paket" : row.row_number),
    },
    { key: "field", header: "Ustun", render: (row) => <span className="mono">{row.field || "—"}</span> },
    { key: "value", header: "Qiymat", render: (row) => <span className="mono">{row.value || "—"}</span> },
    { key: "code", header: "Xato", render: (row) => <StatusBadge value={row.error_code} tone="error" /> },
    { key: "message", header: "Sabab", render: (row) => row.message },
  ];

  const batch = outcome?.batch;
  const validated = batch?.status === "VALIDATED" && !outcome?.committed;
  const checks = batch?.control_totals?.checks ?? [];

  return (
    <>
      <PageHeader
        title="Import"
        hint="MIG-01 tartibi: tovarlar → kataklar → boshlang'ich qoldiq. Avval tekshiruv; bitta xato qator butun faylni rad etadi (IMP-001)."
        actions={
          spec.template && session.can(spec.permission) ? (
            <button
              type="button"
              className="btn btn-tonal"
              onClick={() => {
                void downloadFile(spec.service, spec.template!, "yacheyka-import-namuna.xlsx");
              }}
            >
              <Icon name="external" size={16} />
              Namuna XLSX
            </button>
          ) : null
        }
      />

      <div className="toolbar">
        <SegmentedFilter
          value={kind}
          onChange={(value) => {
            setKind(value);
            setFile(null);
            reset();
            if (input.current) input.current.value = "";
          }}
          options={(Object.keys(KINDS) as Kind[]).map((key) => ({
            value: key,
            label: `${KINDS[key].step}. ${KINDS[key].label}`,
          }))}
        />
      </div>

      {kind === "session" ? (
        <SessionImport />
      ) : (
      <>
      <section className="card" style={{ padding: 20, display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{spec.label}</div>
          <div className="field-hint">Ustunlar (o'zbek, rus yoki ingliz sarlavha): {spec.columns}. * — majburiy.</div>
          <div className="field-hint">{spec.hint}</div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={input}
            type="file"
            accept=".xlsx,.csv"
            aria-label="Import fayli"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              reset();
            }}
          />
          <button
            type="button"
            className="btn btn-tonal"
            disabled={!file || busy || !session.can(spec.permission)}
            onClick={() => void run(false)}
          >
            <Icon name="check" size={16} />
            {busy ? "Tekshirilmoqda…" : "Tekshirish"}
          </button>
          <button
            type="button"
            className="btn btn-filled"
            disabled={!validated || busy}
            title={validated ? undefined : "Avval fayl tekshiruvdan o'tishi kerak"}
            onClick={() => setConfirming(true)}
          >
            <Icon name="integration" size={16} />
            Yozish
          </button>
          {!session.can(spec.permission) && (
            <span className="field-hint">Bu import uchun vakolat yo'q ({spec.permission}).</span>
          )}
        </div>

        {failure && (
          <div className="notice" role="alert">
            <Icon name="alert" size={16} />
            <span>{failure}</span>
          </div>
        )}

        {batch && !outcome?.rejected && (
          <div className="notice">
            <Icon name="check" size={16} />
            <span>
              <StatusBadge value={batch.status} /> {batch.source_file} — {batch.total_rows} qator
              {batch.committed_rows !== undefined && outcome?.committed ? `, yozildi ${batch.committed_rows}` : ""}
              {batch.lp_count !== undefined && outcome?.committed ? `, LP ${batch.lp_count}, harakat ${batch.movement_count}` : ""}
              {batch.replayed ? " · bu fayl avval yozilgan — qoldiq qayta yozilmadi" : ""}
              {validated ? ". Xato yo'q — endi «Yozish» mumkin." : ""}
            </span>
          </div>
        )}

        {outcome?.rejected && (
          <div className="notice" role="alert">
            <Icon name="alert" size={16} />
            <span>
              {outcome.rejected.message}
              {batch ? ` — ${batch.rejected_rows} / ${batch.total_rows} qator xato. Hech narsa yozilmadi.` : ""}
              {/* Nazorat yig'indilari jadvali bo'lsa matnda takrorlanmaydi. */}
              {outcome.rejected.failedTotals?.length && checks.length === 0
                ? ` Nazorat: ${outcome.rejected.failedTotals.join("; ")}`
                : ""}
            </span>
            {spec.errorReport &&
              outcome.rejected.batchId &&
              (kind !== "locations" || outcome.errors?.some((error) => error.error_code === "duplicate_code")) && (
              <button
                type="button"
                className="btn btn-text btn-sm"
                onClick={() =>
                  void downloadFile(
                    spec.service,
                    spec.errorReport!(outcome.rejected!.batchId!),
                    `${kind}-xatolar-${outcome.rejected!.batchId!.slice(0, 8)}.xlsx`,
                  ).catch((cause: unknown) => setFailure(String(cause)))
                }
              >
                <Icon name="report" size={14} />
                {kind === "locations" ? "Dublikatlar XLSX" : "Xatolar XLSX"}
              </button>
            )}
          </div>
        )}
      </section>

      {checks.length > 0 && (
        <section style={{ marginTop: 16 }}>
          <DataTable
            columns={[
              { key: "name", header: "Nazorat yig'indisi", render: (row: ControlCheck) => <span className="mono">{row.name}</span> },
              { key: "rule", header: "Qoida", render: (row: ControlCheck) => row.rule ?? "—" },
              { key: "expected", header: "Kutilgan", align: "right", render: (row: ControlCheck) => row.expected },
              { key: "actual", header: "Faylda", align: "right", render: (row: ControlCheck) => row.actual },
              {
                key: "passed",
                header: "Natija",
                render: (row: ControlCheck) =>
                  row.passed ? (
                    <StatusBadge value="mos" tone="success" />
                  ) : (
                    <StatusBadge value={row.blocking ? "mos emas" : "ma'lumot uchun"} tone={row.blocking ? "error" : "warning"} />
                  ),
              },
            ]}
            rows={checks}
            rowKey={(row) => row.name}
          />
        </section>
      )}

      {outcome?.errors && (
        <section style={{ marginTop: 16 }}>
          <DataTable
            columns={errorColumns}
            rows={outcome.errors}
            rowKey={(row, index) => `${row.row_number}-${row.field}-${index}`}
            emptyTitle="Qator xatosi yo'q"
            emptyHint="Paket darajasida rad etildi — yuqoridagi nazorat yig'indilariga qarang."
            hasMore={outcome.moreErrors}
            footer={
              <span>
                {outcome.errors.length} ta xato{outcome.moreErrors ? " (birinchilari; to'liq ro'yxat XLSX da)" : ""}
                {batch?.created_at ? ` · ${dateTime(batch.created_at)}` : ""}
              </span>
            }
          />
        </section>
      )}

      </>
      )}

      <ConfirmDialog
        open={confirming}
        title={`${spec.label}: faylni yozish`}
        description={
          <>
            <b>{file?.name}</b> tekshiruvdan o'tdi ({batch?.total_rows ?? 0} qator). Yozilgach ma'lumot
            {kind === "opening" ? " qoldiq va harakat sifatida hisobga kiradi" : " reyestrga kiradi va hodisalar e'lon qilinadi"}.
          </>
        }
        confirmLabel="Yozish"
        busy={busy}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void run(true);
        }}
      />
    </>
  );
}
